import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Input,
  Button,
  Divider,
  Link,
  Chip
} from '@heroui/react';
import { Eye, EyeOff, Lock, User, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import SignupPage from './SignupPage';
import ForgotPasswordPage from './ForgotPasswordPage';
import ugovLogo from '../../assets/logo.png';

const LoginPage = () => {
  const { checkAuthStatus } = useAuth();
  const [currentView, setCurrentView] = useState('login'); // 'login', 'signup', 'forgot'
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Check for URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const registrationSuccess = urlParams.get('registrationSuccess');
    const inviteCode = urlParams.get('invite_code');
    
    if (registrationSuccess === 'true') {
      setSuccessMessage('Conta ativada. Bem-vindo ao ugov! Agora você pode fazer login.');
    }
    
    if (inviteCode) {
      setCurrentView('signup');
    }
  }, []);

  const toggleVisibility = () => setIsVisible(!isVisible);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username.trim()) {
      setError('Por favor, digite seu usuário ou email');
      return;
    }
    
    if (!formData.password) {
      setError('Por favor, digite sua senha');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      console.log('LoginPage: Attempting login with:', formData.username);
      
      // Use the same endpoint as the old LoginForm
      const formDataToSend = new FormData();
      formDataToSend.append('username', formData.username);
      formDataToSend.append('password', formData.password);
      
      const response = await fetch('/api/v1/users/login.json', {
        method: 'POST',
        credentials: 'include',
        body: formDataToSend
      });

      console.log('LoginPage: Login response status:', response.status);

      if (response.status === 401) {
        setError('Login ou senha incorreta');
        return;
      }
      
      if (response.status === 429) {
        setError('Máximo de tentativas excedidas. Por favor, tente novamente mais tarde.');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        console.log('LoginPage: Login response data:', data);
        
        if (data.errors) {
          if (data.errors.toString().indexOf('cancelled') !== -1) {
            setError('Sua conta ainda não foi ativada. Clique no link em sua mensagem de e-mail para completar o registro da conta.');
          } else {
            setError('Erro desconhecido: ' + data.errors.toString());
          }
          return;
        }

        if (data.user) {
          console.log('LoginPage: Login successful for user:', data.user.username);
          
          // Force a re-check of authentication status
          // This will update the AuthContext state properly
          await checkAuthStatus();
          
          console.log('LoginPage: Auth status re-checked, should redirect to timeline');
        } else {
          setError('Resposta inválida do servidor');
        }
        
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || 'Erro no login');
      }
    } catch (error) {
      console.error('LoginPage: Login error:', error);
      setError('Erro de rede. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  if (currentView === 'signup') {
    return <SignupPage onBackToLogin={() => setCurrentView('login')} />;
  }

  if (currentView === 'forgot') {
    return <ForgotPasswordPage onBackToLogin={() => setCurrentView('login')} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src={ugovLogo}
              alt="ugov"
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">ugov</h1>
          <p className="text-gray-600 mt-2">
            Plataforma de democracia participativa
          </p>
        </div>

        <Card className="w-full border border-gray-200 shadow-post bg-white">
          <CardHeader className="pb-4">
            <div className="flex flex-col space-y-1">
              <h2 className="text-2xl font-bold">Entrar</h2>
              <p className="text-gray-600">
                Entre na sua conta para continuar
              </p>
            </div>
          </CardHeader>

          <CardBody className="space-y-4">
            {/* Success Message */}
            {successMessage && (
              <div className="p-3 bg-success-50 border border-success-200 rounded-lg">
                <p className="text-success-700 text-sm">{successMessage}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-danger-600" />
                  <p className="text-danger-700 text-sm">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Username/Email */}
              <Input
                label="Usuário ou Email"
                placeholder="Digite seu usuário ou email"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                startContent={<User className="w-4 h-4 text-gray-400" />}
                variant="bordered"
                isRequired
              />

              {/* Password */}
              <Input
                label="Senha"
                placeholder="Digite sua senha"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                startContent={<Lock className="w-4 h-4 text-gray-400" />}
                endContent={
                  <button
                    className="focus:outline-none"
                    type="button"
                    onClick={toggleVisibility}
                  >
                    {isVisible ? (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                }
                type={isVisible ? "text" : "password"}
                variant="bordered"
                isRequired
              />

              <Button
                type="submit"
                color="primary"
                size="lg"
                className="w-full"
                isLoading={isLoading}
              >
                {isLoading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>

            <Divider />

            <div className="space-y-3 text-center">
              <div>
                <span className="text-gray-600 text-sm">Não tem uma conta? </span>
                <Link
                  color="primary"
                  className="cursor-pointer text-sm"
                  onPress={() => setCurrentView('signup')}
                >
                  Cadastre-se!
                </Link>
              </div>
              
              <div>
                <Link
                  color="primary"
                  className="cursor-pointer text-sm"
                  onPress={() => setCurrentView('forgot')}
                >
                  Esqueceu sua senha?
                </Link>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Debug Info */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 text-center">
            <Chip size="sm" variant="flat" color="default">
              Endpoint: /api/v1/users/login.json
            </Chip>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
